import { Link } from 'react-router'
import Workflow from '../components/Workflow'
import EcosystemPanel from '../components/EcosystemPanel'
import ClientLogos from '../components/ClientLogos'
import { tools } from '../data/tools'
import { useLanguage } from '../i18n/LanguageContext'
import { useT } from '../i18n/useT'

const statValues = ['13', '15+', '7 yrs', '$0']

// icon + tint metadata for the bento cards; copy comes from translations.
const featureMeta = [
  {
    tint: 'bg-emerald-50',
    stroke: '#059669',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.2 12l2 2 3.8-4" />
      </>
    ),
  },
  {
    tint: 'bg-sky-50',
    stroke: '#0284c7',
    icon: (
      <>
        <rect x="3" y="7" width="18" height="11" rx="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 11h18M7 15h4" />
      </>
    ),
  },
  {
    tint: 'bg-amber-50',
    stroke: '#d97706',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7.5h8M8 11h8M8 14.5h5" />
      </>
    ),
  },
  {
    tint: 'bg-violet-50',
    stroke: '#7c3aed',
    icon: (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 6v6c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8V6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12v6c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8v-6" />
      </>
    ),
  },
  {
    tint: 'bg-teal-50',
    stroke: '#0d9488',
    icon: (
      <>
        <circle cx="6" cy="6" r="2.4" />
        <circle cx="18" cy="6" r="2.4" />
        <circle cx="12" cy="18" r="2.4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.2 7.2l2.6 8.4M15.8 7.2l-2.6 8.4M8.4 6h7.2" />
      </>
    ),
  },
  {
    tint: 'bg-rose-50',
    stroke: '#e11d48',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 13a8 8 0 0116 0" />
        <rect x="3" y="13" width="3.6" height="6" rx="1.6" />
        <rect x="17.4" y="13" width="3.6" height="6" rx="1.6" />
      </>
    ),
  },
]

export default function Home() {
  const t = useT()
  const { lang } = useLanguage()
  const fr = lang === 'fr'

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="hero relative overflow-hidden">
        <div className="relative mx-auto max-w-7xl px-8 pt-20 pb-24 grid grid-cols-12 gap-14 items-center">
          <div className="col-span-7">
            <h1 className="text-[56px] leading-[1.08] font-extrabold mb-6">
              {t.home.h1a}
              <span className="text-[#4b5563]">{t.home.h1b}</span>
            </h1>
            <p className="text-lg leading-relaxed text-ink-mute max-w-xl mb-9">
              {t.home.sub}
            </p>
            <div className="flex items-center gap-4 mb-6">
              <Link
                to="/book-a-demo"
                className="rounded-md border border-slate-200 hover:border-slate-300 bg-white transition-colors text-ink font-semibold px-7 py-3.5"
              >
                {t.home.demo}
              </Link>
            </div>
            <p className="text-sm text-ink-mute">{t.home.trust}</p>
          </div>

          {/* signup card */}
          <div id="get-started" className="col-span-5">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_24px_60px_-24px_rgb(15_23_42/0.25)] p-8">
              <h2 className="text-xl font-bold mb-1.5">{t.home.panel.title}</h2>
              <p className="text-sm text-ink-mute mb-6">{t.home.panel.sub}</p>
              <form className="space-y-3.5" onSubmit={(e) => e.preventDefault()}>
                <select
                  required
                  defaultValue=""
                  className="w-full h-11 rounded border border-slate-200 px-3.5 text-[15px] text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
                >
                  <option value="" disabled>
                    {t.home.panel.accountType}
                  </option>
                  <option value="1">{t.home.panel.option1}</option>
                  <option value="2">{t.home.panel.option2}</option>
                </select>
                {[t.home.panel.name, t.home.panel.company, t.home.panel.email].map((ph) => (
                  <input
                    key={ph}
                    type={ph === t.home.panel.email ? 'email' : 'text'}
                    required
                    placeholder={ph}
                    className="w-full h-11 rounded border border-slate-200 px-3.5 text-[15px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
                  />
                ))}
                <button
                  type="submit"
                  className="w-full h-11 rounded bg-brand-600 hover:bg-brand-700 transition-colors text-white font-semibold text-[15px]"
                >
                  {t.home.panel.submit}
                </button>
              </form>
              <p className="flex items-start gap-2 text-xs text-ink-mute mt-4 leading-relaxed">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 shrink-0 mt-px">
                  <circle cx="12" cy="12" r="9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v5M12 16.5h.01" />
                </svg>
                {t.home.panel.pad}
              </p>
              <p className="text-xs text-slate-400 mt-2.5 leading-relaxed">
                {t.home.panel.legal}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Client logos ─────────────────────────────────── */}
      <ClientLogos />

      {/* ── Stats bar ────────────────────────────────────── */}
      <section className="border-y border-slate-100 bg-white">
        <div className="mx-auto max-w-7xl px-8 py-10 grid grid-cols-4 gap-8">
          {statValues.map((v, i) => (
            <div key={v} className="text-center">
              <p className="font-display text-4xl font-extrabold text-brand-600 mb-1">
                {v}
              </p>
              <p className="text-sm text-ink-mute">{t.home.stats[i]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Workflow ─────────────────────────────────────── */}
      <section className="bg-mist">
        <div className="mx-auto max-w-7xl px-8 pt-28 pb-20">
          <div className="text-center max-w-4xl mx-auto mb-24">
            <h2 className="text-4xl font-extrabold mb-4">{t.home.workflow.h}</h2>
            <p className="text-lg text-ink-mute">{t.home.workflow.sub}</p>
          </div>
          <Workflow />
        </div>
      </section>

      {/* ── Ecosystem ────────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="grid grid-cols-12 gap-14 items-center">
            <div className="col-span-5">
              <h2 className="text-4xl font-extrabold mb-5">{t.home.eco.h}</h2>
              <p className="text-lg text-ink-mute leading-relaxed mb-8">
                {t.home.eco.body}
              </p>
              <Link
                to="/integrations"
                className="inline-flex items-center gap-2 font-semibold text-brand-700 hover:text-brand-800"
              >
                {t.home.eco.link}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>
            <div className="col-span-7">
              <EcosystemPanel />
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature grid (bento) ─────────────────────────── */}
      <section className="bg-mist">
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="text-center max-w-4xl mx-auto mb-14">
            <h2 className="text-4xl font-extrabold mb-4">{t.home.bento.h}</h2>
          </div>
          <div className="grid grid-cols-12 gap-6">
            {/* hero card — compliance */}
            <div className="col-span-7 row-span-2 bg-white rounded-2xl border border-slate-100 p-9 flex flex-col hover:shadow-[0_16px_40px_-16px_rgb(15_23_42/0.15)] transition-shadow">
              <div className={`w-12 h-12 rounded-xl ${featureMeta[0].tint} grid place-items-center mb-6`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={featureMeta[0].stroke} strokeWidth={1.8} className="w-7 h-7">
                  {featureMeta[0].icon}
                </svg>
              </div>
              <h3 className="text-2xl font-extrabold mb-3">{t.home.bento.heroTitle}</h3>
              <p className="text-[16px] leading-relaxed text-ink-mute mb-8 max-w-md">
                {t.home.bento.heroBody}
              </p>
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-mute mb-3">
                  {t.home.bento.provLabel}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'].map((p) => (
                    <span
                      key={p}
                      className={`rounded-md text-xs font-bold px-2.5 py-1.5 border ${
                        p === 'QC'
                          ? 'bg-brand-600 border-brand-600 text-white'
                          : 'bg-mist border-slate-200 text-ink'
                      }`}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-auto flex flex-wrap gap-2.5">
                {t.home.bento.docChips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700 text-sm font-semibold px-3.5 py-1.5"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
            {/* supporting cards */}
            {t.home.bento.cards.map((c, i) => (
              <div
                key={c.title}
                className={`${i < 2 ? 'col-span-5' : 'col-span-4'} bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-[0_16px_40px_-16px_rgb(15_23_42/0.15)] transition-shadow`}
              >
                <div className={`w-11 h-11 rounded-xl ${featureMeta[i + 1].tint} grid place-items-center mb-5`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={featureMeta[i + 1].stroke} strokeWidth={1.8} className="w-6 h-6">
                    {featureMeta[i + 1].icon}
                  </svg>
                </div>
                <h3 className="text-lg font-bold mb-2">{c.title}</h3>
                <p className="text-[15px] leading-relaxed text-ink-mute">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 font-semibold text-brand-700 hover:text-brand-800"
            >
              {t.home.bento.link}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Free tools ───────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-4xl font-extrabold mb-4">{t.home.toolsH}</h2>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {tools.map((tool) => (
              <Link
                key={tool.slug}
                to={`/tools/${tool.slug}`}
                className="group rounded-2xl border border-slate-100 p-7 hover:border-brand-300 hover:shadow-[0_16px_40px_-16px_rgb(22_163_74/0.2)] transition-all"
              >
                <h3 className="text-lg font-bold mb-2 group-hover:text-brand-700 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-[15px] leading-relaxed text-ink-mute mb-4">
                  {fr ? tool.bodyFr : tool.body}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700">
                  {t.home.openTool}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA band ─────────────────────────────────────── */}
      <section id="demo" className="bg-gradient-to-br from-brand-700 to-brand-900 text-white">
        <div className="mx-auto max-w-7xl px-8 py-20 text-center">
          <h2 className="text-4xl font-extrabold mb-4 text-white">{t.home.cta.h}</h2>
          <p className="text-lg text-brand-100 max-w-none mx-auto mb-9 whitespace-nowrap">{t.home.cta.sub}</p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/book-a-demo"
              className="rounded-md bg-white text-brand-800 font-semibold px-7 py-3.5 hover:bg-brand-50 transition-colors"
            >
              {t.home.cta.button}
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
